import { css } from "@emotion/react"
import { Button, FileInputButton } from "component/button"
import { ChangeEvent, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AxiosProgressEvent } from "axios"
import { toast } from "react-toastify"
import { NoFileError } from "error"
import { TextField } from "component/TextField"
import { BiShare, BiComment } from 'react-icons/bi'
import { IconContext } from "react-icons"
import { upload } from "utils/common"
import { callApiForRegisterArticle, callApiForRegisterSimplePost, fetchBackendVersion } from "utils/httpApi"
import { HOME_PATH, VERSION } from "config"

export const Upload = () => {
  const navigate = useNavigate()
  const [inputFiles, setInputFiles] = useState<FileList | null>(null)
  const [message, setMessage] = useState('')
  const [isPost, setIsPost] = useState<boolean>(true)
  const [buttonText, setButtonText] = useState<string>('アップロード')
  const [displayedProgress, setDisplayedProgress] = useState<number>(0)
  const [backendVersion, setBackendVersion] = useState<string>('loading...')
  const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false)
  const [serverVersion, setServerVersion] = useState<string | null>(null)

  const appVersion = localStorage.getItem('app_version')
  const buildTime = appVersion ? new Date(Number(appVersion)).toLocaleString() : 'unknown'

  useEffect(() => {
    fetchBackendVersion().then(res => {
      setBackendVersion(res.version)
    }).catch(e => {
      console.error(e)
      setBackendVersion('error')
    })

    const checkUpdate = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const { version } = await res.json();
        const sv = String(version);
        setServerVersion(sv);
        if (appVersion && appVersion !== sv) {
          setIsUpdateAvailable(true);
        }
      } catch (e) {
        console.error('Update check failed:', e);
      }
    };
    checkUpdate();
  }, [appVersion])

  const onUpdateClick = async () => {
    if (!serverVersion) return;
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
    }
    localStorage.setItem('app_version', serverVersion);
    window.location.href = HOME_PATH;
  }


  const onTitleInputChange = (evt: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(evt.target.value)
  }

  const onChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setInputFiles(evt.target.files)
  }

  const onUploadProgress = (event: AxiosProgressEvent) => {
    const currentProgress = event.total ? Math.floor((event.loaded * 100) / event.total) : event.loaded
    setDisplayedProgress(currentProgress)
  }

  const onUploadClick = async () => {
    try {
      if (inputFiles === null) {
        throw new NoFileError('')
      }

      setButtonText('0/' + inputFiles.length)

      const filenames = await upload(
        inputFiles, onUploadProgress,
        i => setButtonText((i + 1) + '/' + inputFiles.length),
        () => setButtonText('アップロード')
      )
      if (filenames === undefined) throw new Error("")

      if (isPost) {
        await callApiForRegisterArticle(message, filenames)
      } else {
        await callApiForRegisterSimplePost(filenames)
      }
      toast.success('Upload is successful!')
      navigate(HOME_PATH)
    } catch (e) {
      if (e instanceof NoFileError) {
        toast.warn('No file is selected')
      } else {
        toast.error('Upload is failed.')
      }
    }
  }

  return (
    <div css={wrapperCss}>
      <IconContext.Provider value={{ size: "1.3rem" }}>
        <div css={uploadContainerCss}>
          <TextField
            icon={<BiComment />}
            placeholder="message"
            onChange={onTitleInputChange}
          />
          <div css={chooseButtonContainerCss}>
            <FileInputButton onChange={onChange} />
          </div>
          <div css={isPostContainerCss}>
            <input id="is-post" type="checkbox" checked={isPost} onChange={() => setIsPost(prev => !prev)} />
            <label htmlFor="is-post" css={labelCss}>インスタ投稿を行う</label>
          </div>
          <div css={uploadButtonContainerCss}>
            <Button onClick={onUploadClick} level={'primary'}><BiShare /> {buttonText}</Button>
          </div>
          <div css={progressContainerCss}>
            <p>アップロード状況</p>
            <progress value={displayedProgress} max="100" />
          </div>
          <div css={versionConatainerCss}>
            <div>Frontend ver: {VERSION}</div>
            <div>Build time: {buildTime}</div>
            <div>Backend ver: {backendVersion}</div>
            {isUpdateAvailable && (
              <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
                <Button onClick={onUpdateClick} level={'primary'}>UPDATE AVAILABLE</Button>
              </div>
            )}
          </div>
        </div>
      </IconContext.Provider>
    </div>
  )
}

const wrapperCss = css`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const uploadContainerCss = css`
  margin: 2rem;
`

const chooseButtonContainerCss = css`
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
`

const uploadButtonContainerCss = css`
  margin-top: 4rem;
  display: flex;
  justify-content: center;
  align-items: center;
`

const progressContainerCss = css`
  margin-top: 1rem ;
  padding: 10px;
  text-align: center;
`
const versionConatainerCss = css`
  padding: 10px;
  text-align: center;
`
const isPostContainerCss = css`
  margin-top: 30px;
  text-align: center;
`

const labelCss = css`
  margin-left: 5px;
`